import "../../mission/MissionsFilters.scss";
import InfiniteScroll from "react-infinite-scroller";
import Loader from "core/loader/LoaderV2";
import ScrollToTop from "core/buttons/scroll-buttons/ScrollToTop";
import { FormattedMessage } from "react-intl";
import { FILTERS_FOR_NAMES, QUERY_STATUS } from "constants/constants";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import Button from "core/buttons/button/Button";
import Icon from "core/icon/Icon";
import { useContext, useEffect, useRef, useState } from "react";
import MissionsToolsModal from "components/modal/MissionsToolsModal";
import "moment/locale/fr";
import InfoContext from "pages/InfoContext";
import {
  jsonToQueryParams,
  renderInitialLoaderOrNoResult,
  sortHandler,
  sortTriggerHelper,
} from "utils/toolsManagerHelper";
import { getFormattedMission } from "utils/formatters";
import { statusHandler } from "utils/helper";
import Proptypes from "prop-types";
import { useMissionContext } from "api/mission/MissionProvider";
import DocumentDownloadModal from "components/modal/DocumentDownloadModal";
import FloatingScrollbar from "core/floating-scrollbar/FloatingScrollbar";

const MissionsToolsByFilters = ({
  userScope,
  getMoreMissions,
  loadMissionStatus,
  getMission,
  selectedMission,
  propsObject,
  modal,
  setModal,
  filters,
  searchMissionsWithFilters,
  workersList,
  clientsList,
}) => {
  const { getNamesByFilters, namesByFilters, missionsByFilters, lang } =
    useContext(InfoContext);
  const { content, total_items: total, page } = missionsByFilters;
  const [sortedMissions, setSortedMissions] = useState(content);
  const [isExportLimitation, setIsExportLimitation] = useState(false);
  const [selectedMissionIds, setSelectedMissionIds] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [activeSortKey, setActiveSortKey] = useState(null);
  const {
    handleDownloadMissionPPT,
    getMissionsExcelFile,
    generateAllMissionsPPT,
    generateAllMissionsPPTStatus,
    setGenerateAllMissionsPPTStatus,
    missionsExcelFileStatus,
    setMissionsExcelFileStatus,
    creationFileStatus,
    setCreationFileStatus,
  } = useMissionContext();
  const tableContainerRef = useRef(null);
  const [sort, setSort] = useState({
    "name": "asc",
    "client-name": "asc",
    "mission-fee": "asc",
  });
  const isGenerateAllMissionsPPTLoadingStatus =
    generateAllMissionsPPTStatus === QUERY_STATUS.LOADING;
  const selectedMissionsCount = isAllSelected
    ? total
    : selectedMissionIds?.length;
  const isPPTLimitReached = selectedMissionsCount > 200;
  const downloadStatus = statusHandler({
    statusList: [
      missionsExcelFileStatus,
      creationFileStatus,
      generateAllMissionsPPTStatus,
    ],
  });
  const onDownloadModalClose = () => {
    setMissionsExcelFileStatus(QUERY_STATUS.INITIAL);
    setCreationFileStatus(QUERY_STATUS.INITIAL);
    setGenerateAllMissionsPPTStatus(QUERY_STATUS.INITIAL);
  };

  const toggleSelectAll = () => {
    setSelectedMissionIds([]);
    setIsAllSelected((prev) => !prev);
  };

  useEffect(() => {
    setSortedMissions(content);
    if (content?.length) {
      loadEntityNames(content);
    } else {
      setSelectedMissionIds([]);
      setIsAllSelected(false);
    }
  }, [content]);

  const getEntityName = (id) => {
    const entity = namesByFilters?.find((item) => item.id === id);
    return entity?.full_name;
  };

  const loadEntityNames = async (list) => {
    const ids = list
      ?.flatMap((elm) => [elm.client_id, elm.worker_id])
      .filter(Boolean);

    await getNamesByFilters({
      types: [FILTERS_FOR_NAMES.CLIENT, FILTERS_FOR_NAMES.PERSON],
      id: ids,
      include_deleted: true,
    });
  };

  const closeModal = () => {
    setModal(false);
  };

  const createSortableLabel = (id, defaultMessage, sortKey) => ({
    key: id,
    value: (
      <>
        <FormattedMessage id={id} defaultMessage={defaultMessage} />
        <Icon
          icon={sortTriggerHelper({
            header: sort?.[sortKey],
            sortCondition: "desc",
          })}
          color="gray-200"
          width={20}
          height={20}
          onClick={() => {
            setActiveSortKey(sortKey);
            sortHandler({
              sortKey,
              currentSort: sort?.[sortKey],
              setSort,
              getter: searchMissionsWithFilters,
              filters,
            });
          }}
        />
      </>
    ),
  });

  const createLabel = (id, defaultMessage) => ({
    key: id,
    value: <FormattedMessage id={id} defaultMessage={defaultMessage} />,
  });

  const header = [
    {
      id: "select",
      minWidth: "2.5rem",
      label: {
        key: "select-all",
        value: (
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id="select-all"
              checked={isAllSelected}
              onChange={toggleSelectAll}
            />
          </div>
        ),
      },
    },
    {
      label: createSortableLabel(
        "mission_title",
        "Intitulé de la mission",
        "name"
      ),
      icon: "missions",
      iconWidth: 20,
      iconHeight: 20,
      id: "mission_title",
    },
    {
      label: createSortableLabel("client", "Client", "client-name"),
      icon: "user",
      iconWidth: 20,
      iconHeight: 20,
      id: "client",
    },
    {
      label: createLabel("consultant_title", "Consultant/Sous-traitant"),
      icon: "user",
      iconWidth: 20,
      iconHeight: 20,
      id: "consultant",
    },
    {
      label: createLabel("category_and_job", "Catégorie & métier"),
      icon: "user",
      iconWidth: 20,
      iconHeight: 20,
      id: "job",
    },
    {
      label: createSortableLabel("TJ", "TJ", "mission-fee"),
      icon: "salary",
      minWidth: "fit-content",
      iconWidth: 20,
      iconHeight: 20,
      id: "tj",
    },
    {
      label: createLabel("duration_2", "Durée"),
      icon: "time",
      minWidth: "14rem",
      iconWidth: 20,
      iconHeight: 20,
      id: "duration",
    },
    {
      label: createLabel("status", "Statut"),
      icon: "loading",
      iconWidth: 20,
      iconHeight: 20,
      id: "status",
    },
  ];

  const loadMissions = () => {
    if (loadMissionStatus !== QUERY_STATUS.LOADING) {
      getMoreMissions({
        page: page + 1,
        orderBy: activeSortKey
          ? `${activeSortKey}-${sort?.[activeSortKey]}`
          : undefined,
      });
    }
  };

  return (
    <div className="section">
      {modal && (
        <MissionsToolsModal
          getMission={getMission}
          missionData={selectedMission}
          closeModal={closeModal}
          userScope={userScope}
          lang={lang}
        />
      )}
      <DocumentDownloadModal
        status={downloadStatus}
        onClose={onDownloadModalClose}
      />
      <ScrollToTop />
      <div>
        {renderInitialLoaderOrNoResult({
          status: loadMissionStatus,
          hasContent: content?.length,
          page,
        })}
        {content?.length > 0 && (
          <>
            <div className="results">
              <div className="title">
                <Typography
                  className="title-3 violet text-uppercase"
                  type="h2"
                  label={
                    <FormattedMessage id="results" defaultMessage="Résultats" />
                  }
                />
                <div className="total">: {total}</div>
              </div>
              <div className="btn-group">
                <div
                  className="export-refs-button"
                  onMouseEnter={() =>
                    isPPTLimitReached && setIsExportLimitation(true)
                  }
                  onMouseLeave={() =>
                    isPPTLimitReached && setIsExportLimitation(false)
                  }
                >
                  {isExportLimitation && (
                    <div className="export-limitation">
                      <span className="label-error">
                        <FormattedMessage
                          id={"ppt_export_limitation"}
                          defaultMessage={
                            "L'export PPT est limité à 200 références maximum. Veuillez affiner votre recherche."
                          }
                        />
                      </span>
                    </div>
                  )}
                  <Button
                    disabled={
                      !selectedMissionsCount ||
                      isPPTLimitReached ||
                      isGenerateAllMissionsPPTLoadingStatus
                    }
                    className="btn-outlined"
                    size="small"
                    color="white"
                    onClick={() =>
                      generateAllMissionsPPT({
                        filters: jsonToQueryParams({
                          ...filters,
                          ids: selectedMissionIds.join(","),
                        }),
                      })
                    }
                  >
                    <Icon icon="pwp" color="orange" height={20} />
                    <FormattedMessage
                      id={`${
                        isGenerateAllMissionsPPTLoadingStatus
                          ? "export_in_progress"
                          : "export_ref_search"
                      }`}
                      defaultMessage={`${
                        isGenerateAllMissionsPPTLoadingStatus
                          ? "Export en cours"
                          : "Exporter en PPT"
                      }`}
                    />
                    {isGenerateAllMissionsPPTLoadingStatus && (
                      <span class="animated_dots"></span>
                    )}
                  </Button>
                </div>
                <Button
                  className="btn-outlined"
                  size="small"
                  color="white"
                  disabled={missionsExcelFileStatus === QUERY_STATUS.LOADING}
                  onClick={() =>
                    getMissionsExcelFile({
                      filters: jsonToQueryParams({
                        ...filters,
                        ids: selectedMissionIds.join(","),
                      }),
                    })
                  }
                >
                  <Icon icon="sheet" color="green" height={20} />
                  <FormattedMessage
                    id={"export_excel"}
                    defaultMessage={"Exporter en Excel"}
                  />
                </Button>
              </div>
            </div>
            <div className="missions-table-container" ref={tableContainerRef}>
              <InfiniteScroll
                pageStart={1}
                loadMore={loadMissions}
                hasMore={content?.length < total}
                loader={content?.length && <Loader key={page} id={page} />}
              >
                <Table
                  id={`missions-by-filters-${page}`}
                  header={header}
                  content={
                    getFormattedMission({
                      handleDownloadMissionPPT,
                      list: sortedMissions,
                      workersList,
                      clientsList,
                      propsObject: {
                        ...propsObject,
                        getEntityName,
                        setIsAllSelected,
                        setSelectedMissionIds,
                        isAllSelected,
                        selectedMissionIds,
                      },
                    }) || []
                  }
                  tableWidth={"100%"}
                  toolsManagerMission={true}
                />
              </InfiniteScroll>
            </div>
            <FloatingScrollbar targetRef={tableContainerRef} />
          </>
        )}
      </div>
    </div>
  );
};

export default MissionsToolsByFilters;

MissionsToolsByFilters.propTypes = {
  missionsByFilters: Proptypes.object,
  userScope: Proptypes.string,
  getMoreMissions: Proptypes.func,
  loadMissionStatus: Proptypes.string,
  getMission: Proptypes.func,
  selectedMission: Proptypes.object,
  propsObject: Proptypes.object,
  modal: Proptypes.bool,
  setModal: Proptypes.func,
  filters: Proptypes.string,
  searchMissionsWithFilters: Proptypes.func,
};
